import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { createAutomation as createAutomationRequest, deleteAutomation as deleteAutomationRequest, editAutomation as editAutomationRequest, pauseAutomation, pinAutomation, resumeAutomation, runAutomation, unpinAutomation, } from '#pplx/frontend/apps/ask/queries/automation-queries/automationActions.js';
import { AUTOMATIONS_MANAGEMENT_PATH, automationQuotaManagementSelectedEvent, connectorTriggerLimitMessages, getConnectorTriggerLimitError, } from '#pplx/frontend/apps/ask/queries/automation-queries/connectorTriggerLimitError.js';
import { getAutomationAdmissionQueryKey } from '#pplx/frontend/apps/ask/queries/automation-queries/useAutomationAdmission.js';
import { getListAutomationRunsQueryKey } from '#pplx/frontend/apps/ask/queries/automation-queries/useListAutomationRuns.js';
import { getListAutomationsQueryKeyPrefix } from '#pplx/frontend/apps/ask/queries/automation-queries/useListAutomations.js';
import { getProjectAutomationsQueryKeyPrefix } from '#pplx/frontend/apps/ask/queries/automation-queries/useProjectAutomations.js';
import { getPinnedAutomationLatestRunLinksQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/automationKeys.js';
import { getPinnedItemsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { runAutomationNow as runLegacyCronNow } from '#pplx/frontend/apps/ask/queries/thread-queries/runAutomationNow.js';
import { updateAutomationState as updateLegacyCronState } from '#pplx/frontend/apps/ask/queries/thread-queries/updateAutomationState.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { MutationObserver, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
const RUN_NOW_COOLDOWN_STATUS = 429;
// Shared key on every automation write so Relay/GraphQL surfaces can subscribe to the mutation cache and refresh their store.
export const AUTOMATION_MUTATION_KEY = ['automation-mutation'];
const AUTOMATION_MUTATION_ACTIONS = [
    'create',
    'edit',
    'move',
    'state',
    'run',
    'delete',
    'pin',
];
const automationMutationActionSet = new Set(AUTOMATION_MUTATION_ACTIONS);
export function isAutomationMutationAction(action) {
    return automationMutationActionSet.has(action);
}
function automationMutationKey(action, automationId) {
    return [...AUTOMATION_MUTATION_KEY, action, automationId];
}
const automationSaveToastMessages = defineMessages({
    projectUpdated: {
        id: '0faf674c75',
        defaultMessage: 'Project updated for “{title}”',
        description: 'Success toast shown after updating an automation Project binding',
    },
    saveCreated: {
        id: 'a93be11bae',
        defaultMessage: '"{title}" created',
        description: 'Success toast shown after creating an automation',
    },
    saveEdited: {
        id: '021ecd5fe6',
        defaultMessage: '"{title}" saved',
        description: 'Success toast shown after saving automation edits',
    },
    saveError: {
        id: 'da594d68ab',
        defaultMessage: 'Unable to save "{title}"',
        description: 'Error toast shown when saving automation changes fails',
    },
});
const automationActionToastMessages = defineMessages({
    pauseSuccess: {
        defaultMessage: '"{title}" paused',
        id: 'a7d5933ddf',
        description: 'Success toast shown when pausing an automation',
    },
    resumeSuccess: {
        defaultMessage: '"{title}" enabled',
        id: '612dddd8ea',
        description: 'Success toast shown when enabling a paused automation',
    },
    runSuccess: {
        defaultMessage: '"{title}" started',
        id: '0d7028b551',
        description: 'Success toast shown when running an automation',
    },
    pauseError: {
        defaultMessage: 'Unable to pause "{title}"',
        id: '658073f7a7',
        description: 'Error toast shown when pausing an automation fails',
    },
    resumeError: {
        defaultMessage: 'Unable to resume "{title}"',
        id: '89ddb1c36b',
        description: 'Error toast shown when resuming an automation fails',
    },
    runError: {
        defaultMessage: 'Unable to run "{title}" now',
        id: 'e0daabb8f5',
        description: 'Error toast shown when running an automation fails',
    },
    runCooldownError: {
        defaultMessage: '"{title}" ran within the last minute; try again shortly.',
        id: '2f5f08fffd',
        description: 'Error toast shown when an automation was run too recently',
    },
    deleteSuccess: {
        defaultMessage: '"{title}" deleted',
        id: '7bb85651be',
        description: 'Success toast shown when deleting an automation',
    },
    deleteError: {
        defaultMessage: 'Unable to delete "{title}"',
        id: 'eca148684e',
        description: 'Error toast shown when deleting an automation fails',
    },
    pinSuccess: {
        defaultMessage: '"{title}" pinned',
        id: 'ac4dda923a',
        description: 'Success toast shown when pinning an automation',
    },
    unpinSuccess: {
        defaultMessage: '"{title}" unpinned',
        id: '03c5cdc30d',
        description: 'Success toast shown when unpinning an automation',
    },
    pinError: {
        defaultMessage: 'Unable to pin "{title}"',
        id: 'bf8e378c95',
        description: 'Error toast shown when pinning an automation fails',
    },
    unpinError: {
        defaultMessage: 'Unable to unpin "{title}"',
        id: 'd8e8d34be5',
        description: 'Error toast shown when unpinning an automation fails',
    },
});
const LEGACY_AUTOMATION_ID_PREFIX = 'legacy_';
export function invalidateAutomationSummaryQueries(queryClient) {
    queryClient.invalidateQueries({ queryKey: getListAutomationsQueryKeyPrefix() });
    queryClient.invalidateQueries({ queryKey: getProjectAutomationsQueryKeyPrefix() });
    queryClient.invalidateQueries({ queryKey: getPinnedAutomationLatestRunLinksQueryKey() });
    queryClient.invalidateQueries({
        queryKey: getPinnedItemsQueryKey(),
        exact: false,
    });
}
function useAutomationSaveToasts(successMessage) {
    const queryClient = useQueryClient();
    const { openToast } = useToastContext();
    const { track } = useInstrumentation();
    const router = useRouter();
    const { $t } = useIntl();
    const onSaveSuccess = useCallback((automation) => {
        invalidateAutomationSummaryQueries(queryClient);
        // edits can change the admission rule; drop the per-automation cache so a
        // mounted detail sidebar or reopened editor refetches it
        queryClient.invalidateQueries({
            queryKey: getAutomationAdmissionQueryKey(automation.automation_id),
        });
        if (successMessage) {
            openToast({
                message: $t(successMessage, { title: automation.name }),
                variant: 'success',
                timeoutSeconds: 3,
                dedupKey: `automation-save-${automation.automation_id}`,
            });
        }
    }, [openToast, queryClient, successMessage, $t]);
    const onSaveError = useCallback((error, title, automationId) => {
        const quotaError = getConnectorTriggerLimitError(error);
        if (quotaError) {
            const { currentCount, limit } = quotaError;
            const hasUsage = currentCount !== null && limit !== null;
            openToast({
                message: $t(connectorTriggerLimitMessages.title),
                description: hasUsage
                    ? $t(connectorTriggerLimitMessages.descriptionWithUsage, {
                        currentCount,
                        limit,
                    })
                    : $t(connectorTriggerLimitMessages.description),
                variant: 'error',
                timeoutSeconds: 0,
                ctaText: $t(connectorTriggerLimitMessages.manageAutomations),
                ctaOnClick: () => {
                    track(automationQuotaManagementSelectedEvent({
                        name: 'automation quota management selected',
                        data: { ...quotaError, surface: 'manage_automation' },
                    }));
                    router.push(AUTOMATIONS_MANAGEMENT_PATH, undefined, 'automation-quota-management');
                },
                dedupKey: `automation-save-error-${automationId ?? 'new'}`,
            });
            return;
        }
        const detailMessage = error instanceof ApiClientsError && typeof error.detail?.message === 'string'
            ? error.detail.message
            : null;
        openToast({
            message: detailMessage ?? $t(automationSaveToastMessages.saveError, { title }),
            // Clear quota-specific content when reusing the save-error dedup key.
            description: undefined,
            ctaText: undefined,
            ctaOnClick: undefined,
            variant: 'error',
            timeoutSeconds: 7,
            dedupKey: `automation-save-error-${automationId ?? 'new'}`,
        });
    }, [openToast, router, track, $t]);
    return { onSaveSuccess, onSaveError };
}
export function useCreateAutomationMutation(callbacks = {}) {
    const { onSaveSuccess, onSaveError } = useAutomationSaveToasts(callbacks.showSuccessToast === false ? undefined : automationSaveToastMessages.saveCreated);
    return useMutation({
        mutationKey: automationMutationKey('create'),
        mutationFn: createAutomationRequest,
        onSuccess: (automation, variables, onMutateResult, context) => {
            onSaveSuccess(automation);
            callbacks.onSuccess?.(automation, variables, onMutateResult, context);
        },
        onError: (error, variables, onMutateResult, context) => {
            if (callbacks.shouldShowErrorToast?.(error, variables) !== false) {
                onSaveError(error, variables.name, undefined);
            }
            callbacks.onError?.(error, variables, onMutateResult, context);
        },
    });
}
export function useUpdateAutomationProjectMutation(callbacks = {}) {
    const { onSaveSuccess, onSaveError } = useAutomationSaveToasts(callbacks.showSuccessToast === false ? undefined : automationSaveToastMessages.projectUpdated);
    return useMutation({
        mutationKey: automationMutationKey('move'),
        mutationFn: (variables) => editAutomationRequest(variables),
        onSuccess: (automation, variables, onMutateResult, context) => {
            onSaveSuccess(automation);
            callbacks.onSuccess?.(automation, variables, onMutateResult, context);
        },
        onError: (error, variables, onMutateResult, context) => {
            onSaveError(error, variables.title, variables.automationId);
            callbacks.onError?.(error, variables, onMutateResult, context);
        },
    });
}
export function useEditAutomationMutation(callbacks = {}) {
    const { onSaveSuccess, onSaveError } = useAutomationSaveToasts(callbacks.showSuccessToast === false ? undefined : automationSaveToastMessages.saveEdited);
    return useMutation({
        mutationKey: automationMutationKey('edit'),
        mutationFn: (variables) => editAutomationRequest(variables, {
            onWebhookCredentials: callbacks.onWebhookCredentials,
        }),
        onSuccess: (automation, variables, onMutateResult, context) => {
            onSaveSuccess(automation);
            callbacks.onSuccess?.(automation, variables, onMutateResult, context);
        },
        onError: (error, variables, onMutateResult, context) => {
            if (callbacks.shouldShowErrorToast?.(error, variables) !== false) {
                const title = 'title' in variables ? variables.title : variables.name;
                onSaveError(error, title, variables.automationId);
            }
            callbacks.onError?.(error, variables, onMutateResult, context);
        },
    });
}
function useAutomationActionOptionsFactory() {
    const queryClient = useQueryClient();
    const { $t } = useIntl();
    const { openToast } = useToastContext();
    return useCallback(({ automationId, isLegacy = false, isPaused, isPinned = false, title, onDeleteSuccess, onPauseResumeSuccess, onRunNowSuccess, onPinSuccess, showRunNowSuccessToast = true, showStateChangeSuccessToasts = true, }) => {
        const invalidateAutomationQueries = (action) => {
            invalidateAutomationSummaryQueries(queryClient);
            if (action === 'run' || action === 'delete') {
                queryClient.invalidateQueries({
                    queryKey: getListAutomationRunsQueryKey(automationId),
                });
            }
        };
        const legacyCronId = automationId.startsWith(LEGACY_AUTOMATION_ID_PREFIX)
            ? automationId.slice(LEGACY_AUTOMATION_ID_PREFIX.length)
            : automationId;
        const pauseOrResume = {
            mutationKey: automationMutationKey('state', automationId),
            mutationFn: async () => {
                if (isLegacy) {
                    return updateLegacyCronState({
                        automationId: legacyCronId,
                        state: isPaused ? 'active' : 'paused',
                    });
                }
                return isPaused ? resumeAutomation({ automationId }) : pauseAutomation({ automationId });
            },
            onSuccess: (response) => {
                invalidateAutomationQueries('state');
                onPauseResumeSuccess?.(response.state);
                if (!showStateChangeSuccessToasts)
                    return;
                openToast({
                    message: $t(isPaused
                        ? automationActionToastMessages.resumeSuccess
                        : automationActionToastMessages.pauseSuccess, { title }),
                    variant: 'success',
                    timeoutSeconds: 3,
                    dedupKey: `automation-state-${automationId}`,
                });
            },
            onError: () => {
                openToast({
                    message: $t(isPaused
                        ? automationActionToastMessages.resumeError
                        : automationActionToastMessages.pauseError, { title }),
                    variant: 'error',
                    timeoutSeconds: 7,
                    dedupKey: `automation-state-error-${automationId}`,
                });
            },
        };
        const runNow = {
            mutationKey: automationMutationKey('run', automationId),
            mutationFn: async () => {
                if (!isLegacy)
                    return runAutomation({ automationId });
                await runLegacyCronNow({ automationId: legacyCronId });
                return undefined;
            },
            onSuccess: (response) => {
                invalidateAutomationQueries('run');
                onRunNowSuccess?.(response);
                if (!showRunNowSuccessToast)
                    return;
                openToast({
                    message: $t(automationActionToastMessages.runSuccess, { title }),
                    variant: 'success',
                    timeoutSeconds: 3,
                    dedupKey: `automation-run-now-${automationId}`,
                });
            },
            onError: (error) => {
                const isRunNowCooldownError = error.status === RUN_NOW_COOLDOWN_STATUS;
                openToast({
                    message: $t(isRunNowCooldownError
                        ? automationActionToastMessages.runCooldownError
                        : automationActionToastMessages.runError, { title }),
                    variant: 'error',
                    timeoutSeconds: 7,
                    dedupKey: `automation-run-now-error-${automationId}`,
                });
            },
        };
        const deleteAutomation = {
            mutationKey: automationMutationKey('delete', automationId),
            mutationFn: () => deleteAutomationRequest({ automationId }),
            onSuccess: () => {
                invalidateAutomationQueries('delete');
                openToast({
                    message: $t(automationActionToastMessages.deleteSuccess, { title }),
                    variant: 'success',
                    timeoutSeconds: 3,
                    dedupKey: `automation-delete-${automationId}`,
                });
                onDeleteSuccess?.();
            },
            onError: () => {
                openToast({
                    message: $t(automationActionToastMessages.deleteError, { title }),
                    variant: 'error',
                    timeoutSeconds: 7,
                    dedupKey: `automation-delete-error-${automationId}`,
                });
            },
        };
        const togglePin = {
            mutationKey: automationMutationKey('pin', automationId),
            mutationFn: () => isPinned ? unpinAutomation({ automationId }) : pinAutomation({ automationId }),
            onSuccess: () => {
                invalidateAutomationQueries('pin');
                onPinSuccess?.();
                if (!showStateChangeSuccessToasts)
                    return;
                openToast({
                    message: $t(isPinned
                        ? automationActionToastMessages.unpinSuccess
                        : automationActionToastMessages.pinSuccess, { title }),
                    variant: 'success',
                    timeoutSeconds: 3,
                    dedupKey: `automation-pin-${automationId}`,
                });
            },
            onError: () => {
                openToast({
                    message: $t(isPinned
                        ? automationActionToastMessages.unpinError
                        : automationActionToastMessages.pinError, { title }),
                    variant: 'error',
                    timeoutSeconds: 7,
                    dedupKey: `automation-pin-error-${automationId}`,
                });
            },
        };
        return { pauseOrResume, runNow, deleteAutomation, togglePin };
    }, [queryClient, $t, openToast]);
}
export function useAutomationActions(args) {
    const options = useAutomationActionOptionsFactory()(args);
    const { mutate: pauseOrResume, isPending: isPauseResumePending } = useMutation(options.pauseOrResume);
    const { mutate: runNow, isPending: isRunNowPending } = useMutation(options.runNow);
    const { mutate: deleteAutomation, isPending: isDeletePending } = useMutation(options.deleteAutomation);
    const { mutate: togglePin, isPending: isPinPending } = useMutation(options.togglePin);
    return {
        pauseOrResume,
        isPauseResumePending,
        runNow,
        isRunNowPending,
        deleteAutomation,
        isDeletePending,
        togglePin,
        isPinPending,
    };
}
export function useAutomationActionsFactory() {
    const queryClient = useQueryClient();
    const getOptions = useAutomationActionOptionsFactory();
    return useCallback((args) => {
        const options = getOptions(args);
        const execute = (action, mutationOptions) => {
            if (queryClient.isMutating({
                mutationKey: automationMutationKey(action, args.automationId),
                exact: true,
            }) > 0)
                return;
            const observer = new MutationObserver(queryClient, mutationOptions);
            return observer
                .mutate(undefined)
                .catch(() => undefined)
                .finally(() => observer.reset());
        };
        return {
            pauseOrResume: () => execute('state', options.pauseOrResume),
            runNow: () => execute('run', options.runNow),
            deleteAutomation: () => execute('delete', options.deleteAutomation),
            togglePin: () => execute('pin', options.togglePin),
        };
    }, [getOptions, queryClient]);
}
//# sourceMappingURL=useAutomationActions.js.map